import { defineStore } from "pinia";
import { commClient } from "fw4-library";
import router from "@app/router";
import { i18n } from "@app/i18n";
import { useFw4Store, MessageBoxPredefinedButtons, MessageBoxButtonType } from "fw4-library";
import SnackSuccessIcon from "@assets/images/general/snackbar/icon-check-circle.svg";
import SnackErrorIcon from "@assets/images/general/snackbar/icon-x-circle.svg";

export interface IKeyValue{
    Key: number | string;
    Value: number | string;
    AdditionalData?: Record<string, any>
}

export interface IUser{
    Username: string;
    FullName: string;
    BusinessUnitName: string;
    PartnerName: string;
    PartnerId: number;
    DIYType: string;
    PartnerLimitsEOM: boolean;
    AvtoBroker: boolean;
    NeedsToChangePassword: boolean;
    CanAccessPortal: boolean;
    CanAccessContract: boolean;
    CanAccessCalc: boolean;
    MinCredit: number;
    MinPlaca: number;
    MinPlacaBruto: number;
    SisbonTraining: number;
    UserDisabled: boolean;
    SisbonId: string;
    CanCompleteContract: boolean;
    DashNotificationId: number;
    ShowDashNotification: boolean;
    IsIdentificationSuccessful: boolean;
}

export interface INotification {
    NotificationId: number;
    NotificationSeen: boolean;
    Title: string;
    Subtitle: string;
    Text: string;
    CreatedAt: string;
}

export interface IAppNotifications{
    NotificationList: INotification[];
    ShownNotification: INotification | undefined;
    ShownNotificationIndex: number;
    NotificationTypeFilter: IKeyValue;
}

export enum IPasswordStatus{
    Success = 1,
    Error = 2,
    Undefined = 3
}

export interface IPasswordChange{
    OldPassword: string;
    NewPassword: string;
    RepeatPassword: string;
    Status: IPasswordStatus;
    DescriptionText: string;
}

export interface IShowNavDrawer{
    ShowDrawerLeft: boolean;
    ShowDrawerRight: boolean;
}

export interface ISnackbar{
    ShowSnackBar: boolean;
    SnackbarText: string;
    Class: string;
    Image: string;
}

// STATE
export interface IAppState{
    SideMenu: boolean;
    User: IUser;
    AppNotifications: IAppNotifications;
    PasswordChange: IPasswordChange;
    ShowNavDrawer: IShowNavDrawer;
    OpenChangePassModal: boolean;
    ShowLoaderMain: boolean;
    Snackbar: ISnackbar;
    DisplayModal: boolean;
}

function DefaultNotificationFilters(): IKeyValue{
    return {
        Key:"All",  
        Value: 'Vsa obvestila'
    };
}

export const useAppStore = defineStore('appStore', {
    state: (): IAppState => {
        return {
            SideMenu: true,
            User: {
                Username: "",
                FullName: "",
                BusinessUnitName: "",
                PartnerName: "",
                PartnerId: -1,
                DIYType: "NONE",
                PartnerLimitsEOM: false,
                AvtoBroker: false,
                NeedsToChangePassword: false,
                CanAccessPortal: false,
                CanAccessContract: false,
                CanAccessCalc: false,
                MinCredit: 0,
                MinPlaca: 0,
                MinPlacaBruto: 0,
                SisbonTraining: 0,
                SisbonId: "",
                UserDisabled: false,
                CanCompleteContract: false,
                DashNotificationId: -1,
                ShowDashNotification: false,
                IsIdentificationSuccessful: false
            },
            AppNotifications: {
                NotificationList: [],
                ShownNotification: undefined,
                ShownNotificationIndex: -1,
                NotificationTypeFilter: DefaultNotificationFilters()
            },
            PasswordChange: {
                OldPassword: "",
                NewPassword: "",
                RepeatPassword: "",
                Status: IPasswordStatus.Success,
                DescriptionText: "Geslo je bilo uspešno zamenjano"
            },
            ShowNavDrawer: {
                ShowDrawerLeft: true,
                ShowDrawerRight: false
            },
            OpenChangePassModal: false,
            ShowLoaderMain: false,
            Snackbar: {
                ShowSnackBar: false,
                SnackbarText: "",
                Class: "",
                Image: ""
            },
            DisplayModal: true
        }
    },
    actions: {
        BackToDashboard(){
            router.push({ name: "dashboard_dashboard"});  
        },

        toggleSideMenu(){
            this.AppNotifications.NotificationTypeFilter = DefaultNotificationFilters();
            this.SideMenu = !this.SideMenu;
        },

        InitialRedirect() {
            if(this.User.NeedsToChangePassword){
                this.OpenChangePassModal = true;
                router.push({name: "dashboard"});
                return;
            } else  if(!this.User.CanAccessPortal && (this.User.SisbonTraining == 2 || this.User.SisbonTraining == 3)){
                router.push({ name: "pogodbe_seznam" });
                return;
            }
        },

        async ShowTrainingPopups() {
            const fw4 = useFw4Store();

            switch (this.User.SisbonTraining) {
            case 1:
                fw4.ShowMessageBox("Obvestilo", "Pozdravljeni,<br /><br />Za sklepanje pogodb je pogoj uspešno opravljeno izobraževanje SISBON, do takrat bo sklepanje pogodb onemogočeno. Za pomoč pri reševanju se lahko obrnete na telefonsko številko 01 548 34 42.<br /><br />Lep pozdrav,<br />Ekipa NLB Buy&Go", [
                    MessageBoxPredefinedButtons[MessageBoxButtonType.OK],
                    { Id: "Train", Caption: "Na izobraževanje", Type: MessageBoxButtonType.OK, IsPrimary: false }
                    ]).then((buttonId: any) => {  
                        if (buttonId == "Train") {
                            router.push({name: "training_home"});
                        } else {
                            this.InitialRedirect();
                        }
                    })
                break;
            case 2:
                await this.ContractCreateConsentLog();
                fw4.ShowMessageBox("Opozorilo", "Vaš profil ni več aktiven, saj v zadnjem mesecu niste vnesli pogodbe.<br/ > <br />Postopek ponovne aktivacije je sedaj v teku. Med delovnim časom bo aktivacija zaključena v nekaj minutah, sicer pa najkasneje naslednji delovni dan - takrat lahko nadaljujete z vnosom pogodb.", [
                    MessageBoxPredefinedButtons[MessageBoxButtonType.Close]
                ])
                break;
            default:
                this.InitialRedirect();
                break;
            }
        },

        async ContractCreateConsentLog() {
            await commClient.executeMethodAsync("ContractCreateConsentLog",{}
            ).then(async (Message: any) => {
            });
        },

        async savePassword() {
            const passwordSaved = (Message: any): void => {
                if (Message && Message.status == "OK") {
                    this.PasswordChange.Status = IPasswordStatus.Success;
                    this.PasswordChange.DescriptionText = i18n.global.t("PasswordChange.Success");

                    this.User.NeedsToChangePassword = false;
                    this.PasswordChange.NewPassword = "";
                    this.PasswordChange.RepeatPassword = "";
                    this.PasswordChange.OldPassword = "";
                    router.push({name: "dashboard"})

                } else if (Message && Message.status == "ERROR") {
                    // show error
                    this.PasswordChange.Status = IPasswordStatus.Error;
                    this.PasswordChange.DescriptionText = i18n.global.t("PasswordChange.Error");
                }
            };

            commClient.executeMethod("PasswordSave", {
                "OldPassword": this.PasswordChange.OldPassword,
                "Password": this.PasswordChange.NewPassword
            }, passwordSaved);
        },
        cancelSavePassword() {
            if(this.User.NeedsToChangePassword == true){
                this.User.NeedsToChangePassword = false;
                this.PasswordChange.NewPassword = "";
                this.PasswordChange.RepeatPassword = "";
                if(commClient.WebSocketConnected){
                    commClient.disconnectWebSockets();
                }
                
                router.push({ name: "home"});
            }
            else{
                router.push({ name: "dashboard_dashboard"});
            }
        },

        ChangePasswordClearValues(){
            this.PasswordChange.Status = IPasswordStatus.Undefined;
            this.PasswordChange.NewPassword = "";
            this.PasswordChange.RepeatPassword = "";
            this.PasswordChange.OldPassword = "";
        },

        async LoadUserData() {
            const loadComplete = (Message: any): void => {
                if (Message) {
                    this.User = Message as IUser;
                    this.GetNotifications("", true);          
                    //this.ShowTrainingPopups();
                    this.InitialRedirect();
                } else {
                }
            };

            commClient.executeMethod("sf.AppDataLoad", {}, loadComplete);
        },

        async GetNotifications(search: string = "", isStartUp: boolean){
            const getComplete = (Message: INotification[]): void => {
                if (Message) {              
                    Message.map((notification: INotification) => {
                        let firstChild = new DOMParser().parseFromString(notification.Text, "text/html").firstChild as HTMLElement;                        
                        notification.Subtitle = firstChild.innerText;
                    });
                    
                    this.AppNotifications.NotificationList = Message;

                    if(isStartUp){
                        this.AppNotifications.NotificationTypeFilter = {Key:"New",  Value: "Novo"};

                        if(this.FilterNotifications.length > 0){
                            this.ShowNavDrawer.ShowDrawerRight = true;
                            this.ShowNotification(0);
                        }
                    }
                } else {
                }
            };

            commClient.executeMethod("sf.ObvestilaGet", {
                Search: search                
            }, getComplete);
        },

        async DashNotificationClose() {
            const closeComplete = (Message: any): void => {
                if (Message) {
                    this.User.ShowDashNotification = false;
                } else {
                }
            };

            commClient.executeMethod("sf.DashNotificationChecked", {
                DashNotificationId: this.User.DashNotificationId
            }, closeComplete);
        },

        ShowNotification(idx: number){
            this.AppNotifications.ShownNotificationIndex = idx;
            this.AppNotifications.ShownNotification = this.FilterNotifications[idx];  
            
            const setComplete = (Message: any): void => {                
                if(Message.Success){
                    this.FilterNotifications[this.AppNotifications.ShownNotificationIndex].NotificationSeen = true;

                    if(this.AppNotifications.NotificationTypeFilter.Key == "New"){
                        this.AppNotifications.ShownNotificationIndex--;
                    }
                }                
            };

            if(!this.AppNotifications.ShownNotification.NotificationSeen){
                commClient.executeMethod("sf.MarkNotificationSeen", {
                    NotificationId: this.AppNotifications.ShownNotification!.NotificationId
                }, setComplete);
            }
        },
        
        ShowSnackBar(text: string, type: string){
            switch(type){
                case "success":
                    this.Snackbar.Class = "snack--success";
                    this.Snackbar.Image = SnackSuccessIcon;
                    break;
                case "error":
                    this.Snackbar.Class = "snack--error";
                    this.Snackbar.Image = SnackErrorIcon;
                    break;
            }

            this.Snackbar.SnackbarText = text;
            this.Snackbar.ShowSnackBar = true;
        }
    },
    getters: {

        GetNewNotifications(): number{
            let newNotifications: number = 0; 

            this.AppNotifications.NotificationList.forEach((notification: INotification) => {
                if (!notification.NotificationSeen){
                    newNotifications++;
                }
            })

            return newNotifications;
        },

        FilterNotifications(): INotification[]{
            switch(this.AppNotifications.NotificationTypeFilter.Key){
                case "New":
                    return this.AppNotifications.NotificationList.filter((notification: INotification) => !notification.NotificationSeen);
                case "Seen":
                    return this.AppNotifications.NotificationList.filter((notification: INotification) => notification.NotificationSeen);
                default:
                    return this.AppNotifications.NotificationList;
            }
        },
    }
});